import type { PlaceContext } from 'shared/mapStyles/domain/placeContext';
import { language as placeLanguage } from 'shared/mapStyles/domain/placeFormulaLanguage';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import type { StorageArea, StoragePlace } from 'shared/storageAreas/domain/storageArea';

/**
 * Builds a {@link PlaceContext} for a single storage place. Package-dependent
 * fields (occupied, packageCount) default to empty because the display text is
 * computed when a storage area is edited, outside of any live map interaction.
 */
export function buildPlaceContext(
  place: StoragePlace,
  storageArea: Pick<StorageArea, 'id' | 'name' | 'status'>,
): PlaceContext {
  return {
    index: place.index,
    disabled: place.disabled,
    occupied: false,
    packageCount: 0,
    maxStackCount: place.maxStackCount,
    maxWeight: place.maxWeight,
    maxHeight: place.maxHeight,
    x: place.x,
    y: place.y,
    length: place.length,
    width: place.width,
    zIndex: place.zIndex,
    accessDirections: place.accessDirections,
    accessFront: (place.accessDirections & 1) === 1,
    accessRight: (place.accessDirections & 2) === 2,
    accessBack: (place.accessDirections & 4) === 4,
    accessLeft: (place.accessDirections & 8) === 8,
    storageArea: {
      id: storageArea.id,
      name: storageArea.name,
      empty: storageArea.status === StorageAreaStatus.Empty,
    },
    column: place.column,
    row: place.row,
  };
}

/**
 * Computes the display text for a storage place by evaluating the given formula
 * against the place's context, using the same formula language as the map style
 * place formulas. Returns an empty string when the formula is empty or fails to
 * evaluate.
 */
export function computeStoragePlaceDisplayText(
  formula: string,
  place: StoragePlace,
  storageArea: Pick<StorageArea, 'id' | 'name' | 'status'>,
): string {
  if (!formula) return '';

  const context = buildPlaceContext(place, storageArea);
  const result = placeLanguage.formulaParser.evaluate(formula, context);

  if (!result.compilation.success) {
    return '';
  }

  return result.text;
}

/**
 * Returns a place's user-facing label: its display text when present, otherwise
 * its index as a string.
 */
export function storagePlaceLabel(place: Pick<StoragePlace, 'index' | 'displayText'>): string {
  return place.displayText ? place.displayText : String(place.index);
}
